import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faChartBar, faCircleNotch, faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { Player } from '../../../domain/entities';
import { TACTICAL_ATTRIBUTES } from '../../../domain/constants';
import { CollapsiblePanel } from '../../shared/CollapsiblePanel';

interface PlayerAttributesPanelProps {
    player: Player;
    onCompare: () => void;
}

// Normalize 0-100 scale to 0-20 scale
const normalizeToScale = (value: number): number => Math.round((value / 100) * 20 * 10) / 10;

// Get colour based on attribute value (0-20 scale)
const getValueColor = (value: number): string => {
    if (value >= 14) return 'var(--positive-color)';      // 14-20 = Green (excellent, 70-100%)
    if (value >= 11) return '#ffeb3b';                     // 11-13 = Yellow (good, 55-69%)
    if (value >= 8) return '#ff9800';                      // 8-10 = Orange (average, 40-54%)
    return 'var(--negative-color)';                         // <8 = Red (poor, <40%)
};

const AttributeCard: React.FC<{ name: string; value: number | '--'; isPositionKey: boolean }> = ({ name, value, isPositionKey }) => {
    const valueColor = value !== '--' ? getValueColor(typeof value === 'number' ? value : 0) : 'var(--text-secondary-color)';

    return (
        <div
            className="attribute-card"
            style={{
                border: isPositionKey && value !== '--' ? `2px solid ${valueColor}` : '1px solid rgba(var(--accent-color-rgb), 0.3)',
                padding: '12px',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: isPositionKey && value !== '--' ? `${valueColor}15` : 'transparent', // 15 = ~9% opacity
            }}
        >
            <span style={{ textTransform: 'capitalize', fontSize: '0.95em', opacity: 0.9 }}>
                {name}
                {isPositionKey && value !== '--' && <span style={{ marginLeft: '6px', color: valueColor, fontSize: '0.8em', fontWeight: 'bold' }}>★ Key</span>}
            </span>
            <span
                style={{
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    color: valueColor,
                    minWidth: '40px',
                    textAlign: 'right'
                }}
            >
                {value === '--' ? '--' : `${value}/20`}
            </span>
        </div>
    );
};

export const PlayerAttributesPanel: React.FC<PlayerAttributesPanelProps> = ({ player, onCompare }) => {

    const attributeGroups = useMemo(() => {
        if (player.position === 'GK') {
             return [
                { title: 'Specialist', icon: faCircleNotch, attributes: ['reflexes', 'handling', 'positioning'] },
                { title: 'Physical', icon: faStar, attributes: ['pace'] },
                { title: 'Technical', icon: faChartBar, attributes: ['passing'] },
            ];
        }
        return [
            { title: 'Technical', icon: faChartBar, attributes: ['passing', 'positioning', 'creativity', 'dribbling', 'finishing'] },
            { title: 'Physical', icon: faStar, attributes: ['tackling', 'marking', 'pace', 'heading'] },
            { title: 'Specialist', icon: faCrosshairs, attributes: ['reflexes', 'handling'] },
        ];
    }, [player.position]);

    const keyPositionalAttrs = useMemo(() => TACTICAL_ATTRIBUTES[player.position]?.key || [], [player.position]);

    const relevantAttrs = useMemo(() => {
        const posAttrs = TACTICAL_ATTRIBUTES[player.position];
        if (!posAttrs) return [];
        return [...posAttrs.key, ...posAttrs.secondary];
    }, [player.position]);

    const { strengths, weaknesses, best, worst, avg } = useMemo(() => {
        // Defensive check: attributes might be missing on old saves
        if (!player.attributes || typeof player.attributes !== 'object') {
            return { strengths: [], weaknesses: [], best: null, worst: null, avg: 0 };
        }

        const allAttributes = Object.entries(player.attributes)
            // FIX: Cast `value` to number to resolve TypeScript error where it was being inferred as 'unknown'.
            .map(([key, value]) => ({ name: key, value: normalizeToScale(Math.round(value as number)) }));

        if (allAttributes.length === 0) return { strengths: [], weaknesses: [], best: null, worst: null, avg: 0 };

        // Filter to only relevant attributes for this position
        const relevantAttributes = allAttributes.filter(attr => relevantAttrs.includes(attr.name));

        if (relevantAttributes.length === 0) return { strengths: [], weaknesses: [], best: null, worst: null, avg: 0 };

        const sortedByValue = [...relevantAttributes].sort((a, b) => b.value - a.value);

        const bestAttr = sortedByValue[0];
        const worstAttr = sortedByValue[sortedByValue.length - 1];
        const avgValue = Math.round(relevantAttributes.reduce((sum, attr) => sum + attr.value, 0) / relevantAttributes.length * 10) / 10;

        const str = sortedByValue.filter(attr => attr.value >= 14).slice(0, 2);
        const weak = sortedByValue.filter(attr => attr.value < 8).slice(-4).reverse();

        return { strengths: str, weaknesses: weak, best: bestAttr, worst: worstAttr, avg: avgValue };
    }, [player.attributes, relevantAttrs]);

    const getAttributeValue = (attr: string) => normalizeToScale(Math.round(player.attributes[attr] || 0));

    return (
        <CollapsiblePanel title="Player Attributes" className="panel player-attributes-panel" panelId={`player-attributes-${player.id}`}>
            <div className="panel-content">
                <div className="position-details-section" style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(var(--accent-color-rgb), 0.1)', borderRadius: '4px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8 }}>Position Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.95em' }}>
                        <div>
                            <span style={{ opacity: 0.7 }}>Position:</span>
                            <div style={{ fontWeight: 600 }}>{player.position}</div>
                        </div>
                        <div>
                            <span style={{ opacity: 0.7 }}>Preferred Foot:</span>
                            <div style={{ fontWeight: 600 }}>{player.favoredFoot}</div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ opacity: 0.7 }}>Position Category:</span>
                            <div style={{ fontWeight: 600 }}>{player.favoredPositionCategory}</div>
                        </div>
                    </div>
                </div>

                <div className="attributes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                    {attributeGroups.map(group => (
                        <div className="attribute-category" key={group.title}>
                            <h4 className="attribute-category-title" style={{ fontSize: '0.9em', textTransform: 'uppercase', opacity: 0.7, marginBottom: '12px', letterSpacing: '0.5px' }}>
                                <FontAwesomeIcon icon={group.icon} style={{ marginRight: '8px', color: 'var(--accent-color)' }} />
                                {group.title}
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {group.attributes.map(attr => {
                                    if (player.position !== 'GK' && group.title === 'Specialist') {
                                        return <AttributeCard key={attr} name={attr} value="--" isPositionKey={false} />;
                                    }
                                    return <AttributeCard key={attr} name={attr} value={getAttributeValue(attr)} isPositionKey={keyPositionalAttrs.includes(attr)} />;
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: 'rgba(var(--accent-color-rgb), 0.05)', borderRadius: '4px', fontSize: '0.85em' }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold', opacity: 0.8 }}>Attribute Scale (0-20):</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div><span style={{ color: 'var(--positive-color)', fontWeight: 'bold' }}>14-20</span> = Excellent</div>
                        <div><span style={{ color: '#ffeb3b', fontWeight: 'bold' }}>11-13</span> = Good</div>
                        <div><span style={{ color: '#ff9800', fontWeight: 'bold' }}>8-10</span> = Average</div>
                        <div><span style={{ color: 'var(--negative-color)', fontWeight: 'bold' }}>&lt;8</span> = Poor</div>
                    </div>
                </div>
            </div>
        </CollapsiblePanel>
    );
};